import { useMemo } from 'react'
import { FollowUpDateField } from '@/modules/sales/components/FollowUpDateField'
import { TimePicker } from '@/components/ui/time-picker'
import {
  isInsideWindow,
  minuteToHm,
  quickRetries,
  type CallingWindow,
  type WallTime,
} from '@/modules/calls/utils/callback-time'
import { cn } from '@/utils/cn'

const chipClass = (active: boolean) =>
  cn(
    'h-8 rounded-full border px-3 text-xs font-medium transition-colors',
    active ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-foreground hover:bg-muted',
  )

/**
 * Callback date + time (Pakistan time). Quick picks fill the fields; "None"
 * appears only when the callback is optional.
 */
export function CallbackPicker({
  value,
  onChange,
  hours,
  required,
}: {
  value: Partial<WallTime> | null
  onChange: (value: Partial<WallTime> | null) => void
  hours: CallingWindow
  required: boolean
}) {
  const picks = useMemo(() => quickRetries(new Date(), hours), [hours])
  const complete = value?.ymd && value?.hm ? (value as WallTime) : null
  const outside = complete ? !isInsideWindow(complete, hours) : false
  const same = (wall: WallTime) => complete?.ymd === wall.ymd && complete?.hm === wall.hm

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {required ? null : (
          <button type="button" className={chipClass(value === null)} onClick={() => onChange(null)}>
            None
          </button>
        )}
        {picks.map((pick) => (
          <button
            key={pick.key}
            type="button"
            className={chipClass(same(pick.wall))}
            onClick={() => onChange(pick.wall)}
          >
            {pick.label} · {pick.wall.hm}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FollowUpDateField id="callbackDate" value={value?.ymd ?? ''} onChange={(ymd) => onChange({ ...value, ymd })} />
        <TimePicker
          id="callbackTime"
          aria-label="Callback time"
          value={value?.hm ?? ''}
          min={minuteToHm(hours.callingStartMinute)}
          max={minuteToHm(Math.min(hours.callingEndMinute, 1439))}
          step={300}
          onChange={(hm) => onChange({ ...value, hm })}
        />
      </div>
      {outside ? <p className="text-xs text-amber-600 dark:text-amber-400">Outside calling hours</p> : null}
    </div>
  )
}
